import { useCallback, useEffect, useRef, useState } from "react";
import { useNotify } from "components/admin/Feedback";

/** Tải dữ liệu mỗi khi deps đổi. intervalMs &gt; 0 thì tự tải lại định kỳ (không hiện trạng thái loading). */
export function useFetch(loader, deps = [], intervalMs = 0) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const requestId = useRef(0);

  const load = useCallback(async (silent = false) => {
    const id = ++requestId.current;
    if (!silent) setLoading(true);
    try {
      const result = await loaderRef.current();
      if (id === requestId.current) {
        setData(result);
        setError(null);
      }
    } catch (e) {
      if (id === requestId.current) setError(e.message);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (intervalMs > 0) {
      const timer = setInterval(() => load(true), intervalMs);
      return () => clearInterval(timer);
    }
    return undefined;
  }, deps);

  return { data, loading, error, reload: load, setData };
}

/** Chạy một thao tác, tự báo lỗi / thành công. Trả về [run, busy]. */
export function useRunner() {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (action, successMessage) => {
      setBusy(true);
      try {
        const result = await action();
        if (successMessage) {
          notify.success(
            typeof successMessage === "function" ? successMessage(result) : successMessage
          );
        }
        return { ok: true, result };
      } catch (e) {
        notify.error(e.message);
        return { ok: false };
      } finally {
        setBusy(false);
      }
    },
    [notify]
  );

  return [run, busy];
}
